import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, Typography, TextField } from "@mui/material";
import { axiosInstance } from "../../../interceptors/AxiosInterceptor";
import {
  Avatar,
  Collapse,
  CircularProgress,
  Card,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import FeatureReviews from "./FeatureReviews";
import { AUTH_SECURE_USER_DETAILS_LS_LEY } from "../../edvenswa.emportal.auth/constants/Constants";



const FeedbackReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [selectedView, setSelectedView] = useState("feedback");
  const [openRow, setOpenRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const user = JSON.parse(sessionStorage.getItem(AUTH_SECURE_USER_DETAILS_LS_LEY));
  const role = user?.roles?.[0];
  const isUserRole = role === "User" || role === "Project Manager";

  const fetchFeedbackData = () => {
    setLoading(true);
    axiosInstance
      .get("/user/feedback/all")
      .then((res) => {
        setReviews(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Error fetching feedback:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedView === "feedback") {
      fetchFeedbackData();
    }
  }, [selectedView]);

  const calculateAverage = (ratings) => {
    if (!ratings || ratings.length === 0) return "0.0";
    const total = ratings.reduce((sum, r) => sum + r, 0);
    return (total / ratings.length).toFixed(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter reviews by fullname only
  const filteredReviews = reviews.filter((review) =>
    review.fullname.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderTable = () => {
    // Check if there are no reviews after applying the filter
    if (filteredReviews.length === 0 && !loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <Typography variant="h6">No data found</Typography>
        </Box>
      );
    }

    switch (selectedView) {
      case "feedback":
        return (
          <Box sx={{ padding: 2 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }} />
                      <TableCell sx={{ fontWeight: "bold" }}>Profile</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Associate Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Designation</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Avg. Rating</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Latest Rating</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Total Reviews</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReviews.map((review, index) => (
                      <React.Fragment key={index}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setOpenRow(openRow === index ? null : index)
                              }
                            >
                              {openRow === index ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Avatar src={review.logo} alt={review.fullname} />
                          </TableCell>
                          <TableCell>{review.fullname}</TableCell>
                          <TableCell>{review.designation}</TableCell>
                          <TableCell>{calculateAverage(review.rating)}</TableCell>
                          <TableCell>
                            {review.rating?.[review.rating.length - 1] ?? "-"}
                          </TableCell>
                          <TableCell>{review.rating?.length ?? 0}</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, borderBottom: 0 }}>
                            <Collapse in={openRow === index} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                  All Feedback
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: "bold" }}>Client Name</TableCell>
                                      <TableCell sx={{ fontWeight: "bold" }}>Client Email</TableCell>
                                      <TableCell sx={{ fontWeight: "bold" }}>Company</TableCell>
                                      <TableCell sx={{ fontWeight: "bold" }}>Rating</TableCell>
                                      <TableCell sx={{ fontWeight: "bold" }}>Feedback</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {review.feedback.map((feedback, i) => (
                                      <TableRow key={i}>
                                        <TableCell>
                                          {review.clientName[i] ?? "—"}
                                        </TableCell>
                                        <TableCell>
                                          {review.clientEmail[i] ?? "—"}
                                        </TableCell>
                                        <TableCell>
                                          {review.company[i] || "—"}
                                        </TableCell>
                                        <TableCell>
                                          {review.rating[i] ?? "—"}
                                        </TableCell>
                                        <TableCell>
                                          {feedback ?? "—"}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );
      case "feature":
        return (
          <Box sx={{ p: 4 }}>
            <FeatureReviews setType={() => console.log("FeatureReviews active")} />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
    {isUserRole ? (
      <Card sx={{ p: 3, textAlign: 'center', mt: 4 }}>
        <Typography variant="h6" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          You don't have permission to view this page.
        </Typography>
      </Card>
      ) : (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginX: 3,
            }}
          >
            <Select
              size="small"
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value)}
              sx={{ mr: 2 }}
            >
              <MenuItem value="feedback">Feedback Reviews</MenuItem>
              <MenuItem value="feature">Feature Reviews</MenuItem>
            </Select>
            {selectedView === "feedback" && (
              <TextField
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name"
                sx={{ width: 200 }}
              />
            )}
          </Box>
          {renderTable()}
        </>
      )}
    </Box>
  );
};

export default FeedbackReviews;
